import React, {useEffect, useRef, useState} from 'react';
import { FileType } from '../../model/FileType';
import { useTranslation } from 'react-i18next';
import Button from '@result/result-ui-components/src/components/Button';
import {rotateImageFile} from "../../utils/photoUtils";

const PhotoGallery = ({
	formField,
	deletePhoto,
	accept = [FileType.PNG, FileType.JPG, FileType.JPEG, FileType.WEBP],
	imageDimensions = { width: 150, height: 50, aspectRatio: 16 / 9, maxSize: 4 },
	initialPhotos = [],
	photoRotations,
	setPhotoRotations
}) => {
	const { t } = useTranslation();
	const [selectedFiles, setSelectedFiles] = useState([]);
	const [uploadedPhotos, setUploadedPhotos] = useState([]);
	const [error, setError] = useState('');
	const [showLightbox, setShowLightbox] = useState(false);
	const [selectedPhoto, setSelectedPhoto] = useState(null);

	useEffect(() => {
		setUploadedPhotos(initialPhotos);
	}, [initialPhotos]);

	useEffect(() => {
		if(photoRotations.length === 0) {
			return;
		}
		handleFileChange([]);
	}, [photoRotations]);

	const handlePhoto = async (_files) => {
		const fileData = [];
		for (const file of _files) {
			const photoRotation = photoRotations[file.preview] ?? 0;
			const formData = new FormData();
			formData.append('photo', photoRotation === 0 ? file.file : await rotateImageFile(file.file, photoRotation * 90));
			fileData.push(formData);
		}

		formField.setValue(fileData, false, true);
	};

	const handleFileChangeEvent = (event) => {
		handleFileChange(event.target.files);
	}

	const handleFileChange = (files) => {
		setError('');
		let invalid = false;
		const newFiles = Array.from(files);
		const _files = [...selectedFiles, ...newFiles];

		const fileObjects = _files.map((file) => {
			if (!file.preview) {
				const correctUploadType = accept.some((accepted) => {
					return file.name.split('.').pop() === accepted.extension;
				});

				if (!correctUploadType) {
					setError(
						t('unsupported_file_type', {
							supportedFileTypes: 'jpg, jpeg, png, webp',
						})
					);
					invalid = true;
				}

				const sizeMegabytes = file.size / 1024 / 1024;
				if (sizeMegabytes > imageDimensions.maxSize) {
					setError(
						t('upload_image_too_large', {
							maxSize: imageDimensions.maxSize,
						})
					);
					invalid = true;
				}

				if (invalid) return;

				return {
					preview: URL.createObjectURL(file),
					file: file,
				};
			} else {
				return file;
			}
		});

		if (invalid) return;

		setSelectedFiles(fileObjects);
		handlePhoto(fileObjects);
	};

	const handleRemovePhoto = (url) => {
		const _files = selectedFiles.filter((file) => file.preview !== url);
		setSelectedFiles(_files);
	};

	const handleDeletePhoto = (photoId) => {
		const _photos = uploadedPhotos.filter((photo) => photo.id !== photoId);
		setUploadedPhotos(_photos);
		deletePhoto(photoId);
	};

	return (
		<div className="photo-gallery">
			<div>
				<label
					className={`photo-input ${error ? 'is-invalid' : ''}`}
					htmlFor="photo-upload">
					<i className="fz fz-plus" />
					<input
						type="file"
						multiple
						onChange={handleFileChangeEvent}
						id="photo-upload"
						style={{ display: 'none' }}
						accept={[
							{ extension: 'jpg' },
							{ extension: 'jpeg' },
							{ extension: 'png' },
							{ extension: 'webp' },
						]}
					/>
				</label>
				{error && (
					<div className="is-size-7 has-text-danger mt-1 has-text-centered">
						<i className="fz fz-exclamation-triangle solid mr-1" />
						{error}
					</div>
				)}
			</div>
			{uploadedPhotos?.map(
				(photo, index) =>
					typeof photo.url === 'string' && (
						<div key={index} className="photo-container">
							<img src={photo.url} alt=""
									 style={{
										 transform: `rotate(${(photoRotations[photo.id] ?? 0) * 90}deg)`,
									 }}
							/>
							<button
								type="button"
								onClick={(e) => {
									e.preventDefault();
									handleDeletePhoto(photo.id);
								}}
								className="button is-danger">
								<i className="fz fz-trash-alt" />
							</button>
							<i
								className="photo-container-preview icon fz fz-eye"
								onClick={() => {
									setSelectedPhoto(photo);
									setShowLightbox(true);
								}}
							/>
						</div>
					)
			)}
			{selectedFiles.map((selectedFile) => (
				<div key={selectedFile.preview} className="photo-container">
					<img src={selectedFile.preview} alt=""
							 style={{
								 transform: `rotate(${(photoRotations[selectedFile.preview] ?? 0) * 90}deg)`,
							 }}
					/>
					<button
						type="button"
						onClick={(e) => {
							e.preventDefault();
							handleRemovePhoto(selectedFile.preview);
						}}
						className="button is-danger">
						<i className="fz fz-trash-alt" />
					</button>
					<i
						className="photo-container-preview icon fz fz-eye"
						onClick={() => {
							setSelectedPhoto(selectedFile);
							setShowLightbox(true);
						}}
					/>
				</div>
			))}
			<Lightbox
				photos={[...uploadedPhotos, ...selectedFiles]}
				selectedPhoto={selectedPhoto}
				showLightbox={showLightbox}
				onClose={() => setShowLightbox(false)}
				photoRotations={photoRotations}
				setPhotoRotations={setPhotoRotations}
			/>
		</div>
	);
};

const Lightbox = ({ showLightbox, onClose, photos, selectedPhoto, photoRotations, setPhotoRotations }) => {
	const [selected, setSelected] = useState('');
	const photoId = selected.id ?? selected.preview;
	const selectedRotation = photoRotations[photoId] ?? 0;

	const rotateLeft = () => {
		let oldRotation = photoRotations[photoId];
		let newRotation = oldRotation == null ? 3 : oldRotation - 1;
		if(newRotation < 0) {
			newRotation = 3;
		}

		let newRotations = {...photoRotations};
		newRotations[photoId] = newRotation;
		setPhotoRotations(newRotations);
	}

	const rotateRight = () => {
		let oldRotation = photoRotations[photoId];
		let newRotation = oldRotation == null ? 1 : ((oldRotation + 1) % 4);

		let newRotations = {...photoRotations};
		newRotations[photoId] = newRotation;
		setPhotoRotations(newRotations);
	}

	useEffect(() => {
		if (selectedPhoto) setSelected(selectedPhoto);
	}, [selectedPhoto]);

	return (
		<>
			<Modal
				showPopup={showLightbox}
				onClose={onClose}
				onRotateLeft={() => rotateLeft()}
				onRotateRight={() => rotateRight()}
				content={() => (
					<div className="photo-modal-body">
						{selected && (
							<div className="photo-modal-container">
								<img
								src={selected.url ?? selected.preview}
								style={{
									transform: `rotate(${selectedRotation * 90}deg)`,
									objectFit: 'contain'
								}}
							/>
							</div>

						)}
						{photos && photos.length > 0 && (
							<div className="photo-gallery-thumbnails">
								{photos.map((photo, index) => (
									<img
										key={index}
										src={photo.url ?? photo.preview}
										onClick={() => setSelected(photo)}
										style={{
											transform: `rotate(${(photoRotations[photo.id ?? photo.preview] ?? 0) * 90}deg)`,
										}}
									/>
								))}
							</div>
						)}
					</div>
				)}
			/>
		</>
	);
};

const Modal = (props) => {
	const { t } = useTranslation();
	useEffect(() => {
		const close = (e) => {
			if (e.keyCode === 27) {
				props.onClose();
			}
		};
		window.addEventListener('keydown', close);
		return () => window.removeEventListener('keydown', close);
	}, []);

	if (!props.showPopup) {
		return null;
	}

	return (
		<div
			className={`modal is-wide ${props.showPopup ? 'is-active' : ''}`}
			onClick={props.onClose}>
			<div className={'modal-card'} onClick={(e) => e.stopPropagation()}>
				<div className={'modal-card-head'}>
					<h1 className={'modal-card-title'}>{t('images')}</h1>
				</div>
				<section className={'modal-card-body'}>{props.content()}</section>
				<div className='modal-card-foot is-flex is-justify-content-space-between'>
					<i className="fz fz-arrow-rotate-left is-clickable has-text-weight-bold" onClick={() => props.onRotateLeft()}/>
					<Button
						color="primary"
						label={t('close')}
						isOutlined={true}
						onClick={props.onClose}
					/>
					<i className="fz fz-arrow-rotate-right is-clickable has-text-weight-bold" onClick={() => props.onRotateRight()}/>
				</div>
			</div>
		</div>
	);
};

export default PhotoGallery;
